import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CNSWorkspace } from "@/models/cns";
import {
  fetchWorkspacesForCurrentUser,
  fetchInvitedWorkspaces,
  createWorkspace as createWorkspaceService,
} from "@/services/workspaceService";

const WORKSPACE_STORAGE_KEY = "cns-current-workspace-id";

interface WorkspaceStore {
  workspaces: CNSWorkspace[];
  currentWorkspaceId: string | null;
  loading: boolean;
  error: string | null;

  fetchWorkspaces: () => Promise<void>;
  /**
   * Refetches owned + invited workspaces from the API. Use after access changes
   * (e.g. invite accepted) while the session is already initialized — {@link fetchWorkspaces}
   * no-ops once the store has finished its first successful load.
   */
  refreshWorkspaces: () => Promise<void>;
  setCurrentWorkspace: (workspaceId: string | null) => void;
  createWorkspace: (name: string, type?: CNSWorkspace["type"]) => Promise<CNSWorkspace>;
  clearError: () => void;
  getCurrentWorkspace: () => CNSWorkspace | undefined;
}

type WorkspaceStoreState = WorkspaceStore & {
  _fetchWorkspacesPromise: Promise<void> | null;
  isWorkspacesInitialized: boolean;
};

export const useWorkspaceStore = create<WorkspaceStoreState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      currentWorkspaceId: null,
      loading: false,
      error: null,
      _fetchWorkspacesPromise: null,
      isWorkspacesInitialized: false,

      fetchWorkspaces: async () => {
        const state = get();
        if (state.isWorkspacesInitialized) return;
        if (state._fetchWorkspacesPromise) {
          await state._fetchWorkspacesPromise;
          return;
        }
        const promise = (async () => {
          set({ loading: true, error: null });
          try {
            const [owned, invited] = await Promise.all([
              fetchWorkspacesForCurrentUser(),
              fetchInvitedWorkspaces(),
            ]);
            const list = [...owned, ...invited];
            set((s) => {
              const currentId = s.currentWorkspaceId;
              const stillValid = currentId && list.some((w) => w.id === currentId);
              return {
                workspaces: list,
                loading: false,
                error: null,
                currentWorkspaceId: stillValid ? currentId : (list[0]?.id ?? null),
                _fetchWorkspacesPromise: null,
                isWorkspacesInitialized: true,
              };
            });
          } catch (e: unknown) {
            set({
              loading: false,
              error: e instanceof Error ? e.message : "Failed to fetch workspaces",
              _fetchWorkspacesPromise: null,
            });
          }
        })();
        set({ _fetchWorkspacesPromise: promise });
        await promise;
      },

      refreshWorkspaces: async () => {
        set({ isWorkspacesInitialized: false, _fetchWorkspacesPromise: null });
        await get().fetchWorkspaces();
      },

      setCurrentWorkspace: (workspaceId) => {
        set({ currentWorkspaceId: workspaceId });
      },

      createWorkspace: async (name, type) => {
        set({ loading: true, error: null });
        try {
          const created = await createWorkspaceService(name, type);
          set((state) => ({
            workspaces: [...state.workspaces, created],
            currentWorkspaceId: created.id,
            loading: false,
            error: null,
          }));
          return created;
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : "Failed to create workspace";
          set({ loading: false, error: message });
          throw e;
        }
      },

      clearError: () => set({ error: null }),

      getCurrentWorkspace: () => {
        const { workspaces, currentWorkspaceId } = get();
        return currentWorkspaceId
          ? workspaces.find((w) => w.id === currentWorkspaceId)
          : undefined;
      },
    }),
    {
      name: WORKSPACE_STORAGE_KEY,
      partialize: (state) => ({ currentWorkspaceId: state.currentWorkspaceId }),
    }
  )
);
